import { trpc } from 'api'
import { DiscountOption } from '@order/orderTypes'
import { useOrderState } from './useOrderState'
import { useCallback, useEffect, useState } from 'react'
import { extractDiscountOrders } from './utils/discountedPrice'
import resolveDiscountPrices from '@order/utils/resolveDiscountPrices'

const hasIntersection = (a: (string | number)[], b: (string | number)[]) => {
  return a.some((id) => b.includes(id))
}

const useUpdateDiscounts = () => {
  const remove = useOrderState((s) => s.removeDiscount)

  const update = useOrderState((s) => s.updateDiscount)
  const applyDiscountedOrderPrices = useOrderState(
    (s) => s.applyDiscountedOrderPrices
  )
  const clearFinalPrices = useOrderState((s) => s.clearFinalPrices)

  const discounts = useOrderState((s) => s.discounts)
  const response = useOrderState((s) => s.result)

  const removeDiscount = trpc.order.discounts.removeDiscount.useMutation()
  const removeDiscountAsync = removeDiscount.mutateAsync

  const handleDiscountedOrders = useCallback(
    (response: unknown) => {
      const orders = extractDiscountOrders(response)
      if (!orders?.length) {
        return
      }
      applyDiscountedOrderPrices(orders)
    },
    [applyDiscountedOrderPrices]
  )

  // A removal without prices must clear, not keep, so a discount still on the
  // order is subtracted from the gross amount again
  const handleRemovedDiscount = useCallback(
    (response: unknown, discount: DiscountOption) => {
      const { prices, clearedIds } = resolveDiscountPrices(
        response,
        discount.input.orders.map(({ id }) => id)
      )
      if (prices.length) {
        applyDiscountedOrderPrices(prices)
        return
      }
      clearFinalPrices(clearedIds)
    },
    [applyDiscountedOrderPrices, clearFinalPrices]
  )

  const applyDiscount = trpc.order.discounts.applyDiscount.useMutation({
    onSuccess: (res, req) => {
      const hasId = 'id' in req
      if (!hasId || !req.id) {
        // Should not happen in case of updates
        return
      }
      handleDiscountedOrders(res)
      update({
        id: req.id,
        ...res,
      })
    },
  })

  const cleanupDiscount = useCallback(
    async (discount: DiscountOption) => {
      try {
        const res = await removeDiscountAsync(discount)
        handleRemovedDiscount(res, discount)
      } catch (e) {
        console.error(e)
      }
      remove(discount.id)
    },
    [remove, removeDiscountAsync, handleRemovedDiscount]
  )

  const [updating, setUpdating] = useState(false)

  const updateDiscounts = async (
    discounts: DiscountOption[],
    onSuccess?: () => void
  ) => {
    setUpdating(true)
    for (const discount of discounts) {
      try {
        const res = await removeDiscountAsync(discount)
        handleRemovedDiscount(res, discount)
      } catch (e) {
        console.error(e)
      }
      try {
        await applyDiscount.mutateAsync(discount)
      } catch (e) {
        cleanupDiscount(discount)
        console.error(e)
      }
    }
    setUpdating(false)
    onSuccess?.()
  }

  useEffect(() => {
    if (!response || !discounts.length) {
      return
    }
    const orderIds = response.orders.map((order) => order.id)

    discounts.forEach((discount) => {
      const discountedOrderIds = discount.input.orders.map((order) => order.id)
      if (!hasIntersection(orderIds, discountedOrderIds)) {
        // Remove discount if there are no orders in common
        cleanupDiscount(discount)
      }
    })
  }, [response, discounts, remove, cleanupDiscount])

  return {
    isLoading: applyDiscount.isPending || removeDiscount.isPending || updating,
    update: updateDiscounts,
  }
}

export default useUpdateDiscounts
